import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Chip, type Theme } from '@mui/material';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { type TFunction } from 'i18next';

import { ProcessResponseDTO, ProcessResponseDTOAssessmentType } from '@/api/generated';
import BaseCell from '@/components/DataGrid/BaseCell';
import FormattedDatetimeCell from '@/components/DataGrid/FormattedDatetimeCell';
import TextCell from '@/components/DataGrid/TextCell';
import UserCell from '@/components/DataGrid/UserCell';
import LocationDisplay from '@/components/fields/Location/LocationDisplay';
import StatusDisplay from '@/containers/Organizations/StatusDisplay';
import { toProcess } from '@/root/linker';

const columnHelper = createColumnHelper<ProcessResponseDTO>();

const getProcessType = (processType: string, t: TFunction) => {
  switch (processType) {
    case ProcessResponseDTOAssessmentType.EXTERNAL:
      return t('layouts.processes.details.info.processTypes.external');
    case ProcessResponseDTOAssessmentType.INTERNAL:
      return t('layouts.processes.details.info.processTypes.internal');
    case ProcessResponseDTOAssessmentType.ANY:
      return t('layouts.processes.details.info.processTypes.any');
    default:
      return '';
  }
};

// eslint-disable-next-line arrow-body-style
export const processColumns = (_theme: Theme, t: TFunction) => {
  return [
    columnHelper.accessor('name', {
      header: () => t('layouts.processes.columns.processName'),
      cell: ({ getValue, row }) => (
        <Box
          component={Link}
          to={toProcess({ processId: row.original.id! })}
          sx={{
            cursor: 'pointer',
            textDecoration: 'none',
            color: 'inherit',
            '& .MuiChip-root': {
              mx: 1
            },
            '& .TextCell-label': {
              whiteSpace: 'nowrap'
            },
            '&:hover .TextCell-label': {
              textDecoration: 'underline',
              color: 'primary.main'
            }
          }}
        >
          <TextCell
            tag={
              <>
                <Chip
                  variant="tag"
                  label={getProcessType(row.original.assessmentType!, t)}
                  sx={{ textTransform: 'lowercase' }}
                />
              </>
            }
          >
            {getValue()}
          </TextCell>
        </Box>
      )
    }),

    columnHelper.accessor('status', {
      header: () => t('layouts.processes.columns.status'),
      cell: ({ getValue }) => (
        <BaseCell>
          <StatusDisplay value={getValue()!} />
        </BaseCell>
      )
    }),

    // FIXME: Remove mock data
    // columnHelper.display({
    //   id: 'testTemplates',
    //   header: () => t('layouts.processes.columns.tests'),
    //   cell: () => (
    //     <BaseCell>{MOCK_PROCESS_TEST_TEMPLATES?.map(({ id }) => <TestCategory key={id} value={id!} />)}</BaseCell>
    //   ),
    //   enableSorting: false
    // }),

    columnHelper.accessor('talentsCount', {
      header: () => t('layouts.processes.columns.numberOfTalents'),
      cell: ({ getValue }) => <TextCell>{getValue()}</TextCell>
    }),

    // FIXME: Remove mock data
    // columnHelper.display({
    //   id: 'talentsProgress',
    //   header: () => (
    //     <Stack direction="row" alignItems="center" gap={1}>
    //       {t('layouts.processes.columns.talentsProgress.header')}
    //       <StandardPopper
    //         placement="bottom-end"
    //         caretEndPosition={spacing(1)}
    //         content={
    //           <Paper
    //             sx={{
    //               mt: 1,
    //               p: spacing(0.75, 1.5),
    //               position: 'relative',
    //               maxWidth: '240px',
    //               '.StandardPopper-root &': {
    //                 bgcolor: palette.primary[100]
    //               }
    //             }}
    //           >
    //             <Typography sx={typography.body3}>{t('layouts.processes.columns.talentsProgress.tooltip')}</Typography>
    //           </Paper>
    //         }
    //       >
    //         <IconButton
    //           sx={{
    //             p: 0,
    //             width: spacing(2),
    //             height: spacing(2),
    //             '& .InfoIcon-root': { width: spacing(2), height: spacing(2) }
    //           }}
    //         >
    //           <InfoIcon className="InfoIcon-root" />
    //         </IconButton>
    //       </StandardPopper>
    //     </Stack>
    //   ),
    //   cell: ({ row }) => (
    //     <BaseCell>
    //       <ProgressTalentDisplay id={row.original.id} />
    //     </BaseCell>
    //   ),
    //   enableSorting: false
    // }),

    columnHelper.display({
      id: 'location',
      header: () => t('layouts.processes.columns.location'),
      cell: ({ row }) => (
        <BaseCell>
          <LocationDisplay organization={row.original.organization} location={row.original.location} outlined />
        </BaseCell>
      )
    }),

    columnHelper.accessor('createdBy', {
      header: () => t('layouts.processes.columns.createdBy'),
      cell: ({ getValue }) => <UserCell {...getValue()!} />
    }),

    columnHelper.accessor('createdAt', {
      header: () => t('layouts.processes.columns.creationDate'),
      cell: ({ getValue }) => <FormattedDatetimeCell value={getValue()} variant="datetime" />
    }),

    columnHelper.accessor('updatedAt', {
      header: () => t('layouts.processes.columns.lastUpdate'),
      cell: ({ getValue }) => <FormattedDatetimeCell value={getValue() ?? undefined} variant="datetime" />
    })
  ] as ColumnDef<ProcessResponseDTO>[];
};
